// ═══════════════════════════════════════════════════════════════
// PLANNER ITEMS — calendar events, bulletin notes and My Day blocks.
// They live in world_json.surface_items next to desk docs, tagged with
// `kind` so they never pass for the hotspot placeholders Unity binds to.
// Pure functions, no React.
// ═══════════════════════════════════════════════════════════════

export const PLANNER_KINDS = {
  event: "calendar_event",
  note: "bulletin_note",
  block: "day_block",
  dayEnd: "day_end",
};

const KIND_VALUES = new Set(Object.values(PLANNER_KINDS));

/** A dated note with no time shows up in My Day at this time. */
export const DEFAULT_NOTE_TIME = "06:00";

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const TIME_RE = /^([01]\d|2[0-3]):[0-5]\d$/;

export const isDateKey = (value) => typeof value === "string" && DATE_RE.test(value);
export const isTimeKey = (value) => typeof value === "string" && TIME_RE.test(value);

/** Local calendar day as "YYYY-MM-DD". toISOString would shift it to UTC. */
export function toDateKey(date = new Date()) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

export const timeToMinutes = (time) => {
  const [h, m] = time.split(":").map(Number);
  return h * 60 + m;
};

const newId = (prefix) => `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
const cleanText = (value) => String(value ?? "").trim();

export const isPlannerItem = (item) => KIND_VALUES.has(item?.kind);
export const isCalendarEvent = (item) => item?.kind === PLANNER_KINDS.event;
export const isDayBlock = (item) => item?.kind === PLANNER_KINDS.block;
export const isProposedBlock = (item) => isDayBlock(item) && !!item.proposal;
export const isDayEnd = (item) => item?.kind === PLANNER_KINDS.dayEnd;

/** Brain Dump writes room to-dos as plain bulletin_board items; they count as notes too. */
export const isBulletinNote = (item) =>
  item?.kind === PLANNER_KINDS.note ||
  (!item?.kind && item?.surface === "bulletin_board" && item?.type === "todo" && !!item?.room_id);

// ── Calendar events ─────────────────────────────────────────────

function eventFields(input) {
  const fields = {};
  if ("title" in input) fields.title = cleanText(input.title);
  if ("description" in input) fields.content = cleanText(input.description);
  if ("date" in input) fields.date = input.date;
  if ("start_time" in input) fields.start_time = input.start_time;
  if ("end_time" in input) fields.end_time = input.end_time;
  if ("status" in input) fields.status = input.status;
  return fields;
}

const isValidEvent = (e) =>
  !!e.title &&
  isDateKey(e.date) &&
  isTimeKey(e.start_time) &&
  isTimeKey(e.end_time) &&
  timeToMinutes(e.end_time) > timeToMinutes(e.start_time) &&
  (e.status === "confirmed" || e.status === "pending");

/** Returns the new event, or null when the input is incomplete. */
export function createCalendarEvent(input, source = "user") {
  const event = {
    id: newId("event"),
    kind: PLANNER_KINDS.event,
    surface: "calendar",
    room_id: null,
    title: "",
    content: "",
    status: "confirmed",
    source,
    timestamp: Date.now(),
    ...eventFields(input),
  };
  return isValidEvent(event) ? event : null;
}

export function updateCalendarEvent(event, patch) {
  const next = { ...event, ...eventFields(patch), timestamp: Date.now() };
  return isValidEvent(next) ? next : null;
}

export function eventsForDate(items, dateKey) {
  return (items || [])
    .filter((i) => isCalendarEvent(i) && i.date === dateKey)
    .sort((a, b) => a.start_time.localeCompare(b.start_time));
}

/** One line per event for Lumen's context. */
export function describeCalendarEvent(event) {
  const status = event.status === "pending" ? " (not confirmed)" : "";
  return `${event.date} ${event.start_time}-${event.end_time} ${event.title}${status}`;
}

// ── Bulletin notes ──────────────────────────────────────────────

function noteFields(input) {
  const fields = {};
  if ("content" in input) fields.content = cleanText(input.content);
  if ("date" in input) fields.date = input.date || null;
  if ("time" in input) fields.time = input.time || null;
  if ("done" in input) fields.done = !!input.done;
  return fields;
}

const isValidNote = (n) =>
  !!n.room_id &&
  !!n.content &&
  (n.date === null || isDateKey(n.date)) &&
  (n.time === null || isTimeKey(n.time)) &&
  !(n.time && !n.date);

export function createBulletinNote(input, source = "user") {
  const note = {
    id: newId("note"),
    kind: PLANNER_KINDS.note,
    surface: "bulletin_board",
    room_id: input.room_id || null,
    content: "",
    date: null,
    time: null,
    done: false,
    source,
    timestamp: Date.now(),
    ...noteFields(input),
  };
  return isValidNote(note) ? note : null;
}

/** Upgrades a Brain Dump to-do to a full note the first time it changes. */
export function updateBulletinNote(note, patch) {
  const next = {
    date: null,
    time: null,
    done: false,
    ...note,
    kind: PLANNER_KINDS.note,
    ...noteFields(patch),
    timestamp: Date.now(),
  };
  return isValidNote(next) ? next : null;
}

export function formatWhen(note) {
  if (!isDateKey(note.date)) return null;
  const [y, m, d] = note.date.split("-").map(Number);
  const day = new Date(y, m - 1, d).toLocaleDateString("en-US", { month: "short", day: "numeric" });
  return note.time ? `${day} · ${note.time}` : day;
}

export function notesForRoom(items, roomId) {
  if (!roomId) return [];
  return (items || []).filter((i) => isBulletinNote(i) && i.room_id === roomId);
}

/** Notes from every room that are dated for this day, with the time My Day should use. */
export function datedNotesForDate(items, dateKey) {
  return (items || [])
    .filter((i) => isBulletinNote(i) && i.date === dateKey)
    .map((note) => ({ note, time: note.time || DEFAULT_NOTE_TIME }))
    .sort((a, b) => a.time.localeCompare(b.time));
}

// ── My Day blocks ───────────────────────────────────────────────

export const BLOCK_TYPES = ["task", "break"];

function blockFields(input) {
  const fields = {};
  if ("title" in input) fields.title = cleanText(input.title);
  if ("type" in input) fields.type = input.type;
  if ("date" in input) fields.date = input.date;
  if ("start_time" in input) fields.start_time = input.start_time;
  if ("duration_minutes" in input) fields.duration_minutes = Number(input.duration_minutes);
  if ("done" in input) fields.done = !!input.done;
  if ("displaced_from" in input) fields.displaced_from = input.displaced_from || null;
  if ("displaced_start" in input) fields.displaced_start = input.displaced_start || null;
  if ("proposal" in input) fields.proposal = input.proposal || null;
  if ("from_block" in input) fields.from_block = input.from_block || null;
  if ("planned_by" in input) fields.planned_by = input.planned_by || null;
  if ("plan_date" in input) fields.plan_date = input.plan_date || null;
  return fields;
}

const isValidBlock = (b) =>
  !!b.title &&
  BLOCK_TYPES.includes(b.type) &&
  isDateKey(b.date) &&
  isTimeKey(b.start_time) &&
  Number.isInteger(b.duration_minutes) &&
  b.duration_minutes > 0 &&
  timeToMinutes(b.start_time) + b.duration_minutes <= 24 * 60;

export function createDayBlock(input) {
  const block = {
    id: newId("block"),
    kind: PLANNER_KINDS.block,
    surface: "my_day",
    room_id: null,
    title: "",
    type: "task",
    done: false,
    timestamp: Date.now(),
    ...blockFields(input),
  };
  return isValidBlock(block) ? block : null;
}

export function updateDayBlock(block, patch) {
  const next = { ...block, ...blockFields(patch), timestamp: Date.now() };
  return isValidBlock(next) ? next : null;
}

export function blocksForDate(items, dateKey) {
  return (items || [])
    .filter((i) => isDayBlock(i) && !i.proposal && i.date === dateKey)
    .sort((a, b) => a.start_time.localeCompare(b.start_time));
}

export function proposedBlocksForDate(items, dateKey) {
  return (items || [])
    .filter((i) => isProposedBlock(i) && i.date === dateKey)
    .sort((a, b) => a.start_time.localeCompare(b.start_time));
}

export const DEFAULT_DAY_END = "18:00";

export function createDayEnd(dateKey, endTime) {
  if (!isDateKey(dateKey) || !isTimeKey(endTime)) return null;
  return {
    id: `dayend-${dateKey}`,
    kind: PLANNER_KINDS.dayEnd,
    surface: "my_day",
    room_id: null,
    date: dateKey,
    end_time: endTime,
    timestamp: Date.now(),
  };
}

export function dayEndForDate(items, dateKey) {
  const marker = (items || []).find((i) => isDayEnd(i) && i.date === dateKey);
  return marker?.end_time || DEFAULT_DAY_END;
}
